import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useResponsive } from '../hooks/useResponsive';

const PainPointSequence = ({ onComplete, onPositionChange, onIndexChange }) => {
  const viewport = useResponsive();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const painPointRef = useRef(null);

  const painPoints = useMemo(() => [
    // PHASE 1: RECOGNITION (quick fire questions)
    { text: "Did we collect that skip?", size: '26px', duration: 850 },
    { text: "Have we got one there?", size: '26px', duration: 850 },
    { text: "Did they pay?", size: '26px', duration: 800 },
    { text: "Where is it now?", size: '26px', duration: 850 },

    // PHASE 2: EMPATHY (slight pause, builds)
    { text: "Sound familiar?", size: '28px', duration: 1000 },
    { text: "Tracking skips isn't easy", size: '28px', duration: 1050 },
    { text: "Things slip through", size: '28px', duration: 1000 },

    // PHASE 3: SOLUTION (growing confidence)
    { text: "There is a better way", size: '30px', duration: 1100 },
    { text: "Every skip. One map.", size: '32px', duration: 1300, fontWeight: 'bold' },
    { text: "Always current", size: '30px', duration: 1000 },
    { text: "Always accurate", size: '30px', duration: 1000 },

    // PHASE 4: VALUE (rapid, impactful)
    { text: "Simple", size: '34px', duration: 600, singleWord: true },
    { text: "Clear", size: '34px', duration: 600, singleWord: true },
    { text: "Fast", size: '34px', duration: 550, singleWord: true },

    // PHASE 5: BRAND (settling)
    { text: "This is SkipEye", size: '32px', duration: 1200, fontWeight: 'bold' },
    { text: "Your fleet, at a glance", size: '28px', duration: 1200 },

    // PHASE 6: CTA (largest, longest pause)
    { text: "Ready?", size: '38px', duration: 1800, fontWeight: 'bold', singleWord: true }
  ], []);

  // Calculate responsive positions based on viewport
  const getResponsivePositions = useMemo(() => {
    // Predefined positions adapted for different screen sizes
    // Avoiding: center area (logo), top area (title), button area (65-70% on mobile)
    // Safe zones: top 20-85%, sides 5-70% (mobile) or 5-85% (desktop)
    const basePositions = [
      { top: '25%', left: '10%' },
      { top: '30%', right: '8%' },
      { top: '75%', left: '10%' },
      { top: '80%', right: '15%' },
      { top: '35%', left: viewport.isMobile ? '8%' : '5%' },
      { top: '75%', left: '60%' },
      { top: '85%', right: '10%' },
      { top: '28%', right: '12%' },
      { top: '40%', left: '15%' },
      { top: '78%', right: '8%' },
      { top: '32%', left: viewport.isMobile ? '50%' : '55%' },
      { top: '80%', left: '50%' },
      { top: '38%', right: '5%' },
      { top: '85%', left: '15%' },
      { top: '25%', left: '8%' },
      { top: '75%', right: '12%' },
      { top: '30%', right: '10%' },
      { top: '78%', left: '12%' },
      { top: '42%', right: '15%' },
      { top: '82%', left: '8%' },
      { top: '35%', left: viewport.isMobile ? '8%' : '5%' },
      { top: '76%', right: '5%' },
      { top: '88%', right: '12%' },
      { top: '28%', left: '8%' },
      { top: '82%', left: viewport.isMobile ? '45%' : '55%' },
      { top: '34%', right: '18%' }
    ];

    return basePositions;
  }, [viewport.isMobile]);

  useEffect(() => {
    // Check if we've reached the end
    if (currentIndex >= painPoints.length) {
      return; // Nothing to do, we're past the end
    }

    // Notify parent of index change
    if (onIndexChange) {
      onIndexChange(currentIndex);
    }

    const currentPainPoint = painPoints[currentIndex];
    const displayDuration = currentPainPoint.duration || 1000;
    const transitionGap = 350; // Tighter gap between messages
    const totalDuration = displayDuration + transitionGap;
    const isLastPainPoint = currentIndex === painPoints.length - 1;

    // Fade in (quicker start)
    const fadeInTimer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    // For the last pain point, stay visible. For others, fade out
    const fadeOutTimer = !isLastPainPoint ? setTimeout(() => {
      setIsVisible(false);
    }, displayDuration) : null;

    // Move to next pain point (but stop at the last one)
    const nextTimer = !isLastPainPoint ? setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, totalDuration) : null;

    // If this is the last pain point, call onComplete after display duration
    const completeTimer = isLastPainPoint ? setTimeout(() => {
      if (onComplete) onComplete();
    }, displayDuration) : null;

    return () => {
      clearTimeout(fadeInTimer);
      if (fadeOutTimer) clearTimeout(fadeOutTimer);
      if (nextTimer) clearTimeout(nextTimer);
      if (completeTimer) clearTimeout(completeTimer);
    };
  }, [currentIndex, painPoints, onComplete, onIndexChange]);

  // Track pain point position and notify parent
  useEffect(() => {
    if (isVisible && painPointRef.current && onPositionChange) {
      // Wait for CSS transition to complete (500ms transition + buffer)
      // This ensures we get the final position after fade-in and scale animations
      const timer = setTimeout(() => {
        if (painPointRef.current) {
          const rect = painPointRef.current.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          onPositionChange({ x: centerX, y: centerY });
        }
      }, 550);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, currentIndex]);

  // Clamp to last pain point if we've gone past the end
  const displayIndex = Math.min(currentIndex, painPoints.length - 1);

  if (painPoints.length === 0) {
    return null;
  }

  const currentPainPoint = painPoints[displayIndex];
  const currentPosition = getResponsivePositions[displayIndex];

  // Calculate responsive font size - reduce by ~30% on mobile
  const getResponsiveFontSize = (baseSize) => {
    const sizeValue = parseInt(baseSize);
    if (viewport.isSmallMobile) {
      return `${Math.round(sizeValue * 0.6)}px`;
    }
    if (viewport.isMobile) {
      return `${Math.round(sizeValue * 0.7)}px`;
    }
    return baseSize;
  };

  // Tighter padding for single words
  const getPadding = () => {
    if (currentPainPoint.singleWord) {
      return viewport.isMobile ? '10px 24px' : '12px 32px';
    }
    return viewport.isMobile ? '10px 18px' : '12px 24px';
  };

  const styles = {
    container: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 1000
    },
    painPoint: {
      position: 'absolute',
      ...currentPosition,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: getResponsiveFontSize(currentPainPoint.size),
      fontWeight: currentPainPoint.fontWeight || '500',
      color: 'white',
      padding: getPadding(),
      backgroundColor: '#166534',
      borderRadius: '6px',
      boxShadow: '0 3px 12px rgba(22, 101, 52, 0.2)',
      maxWidth: viewport.isMobile ? '85%' : '500px',
      textAlign: 'center',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'scale(1)' : 'scale(0.92)',
      transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
      whiteSpace: viewport.isSmallMobile ? 'normal' : 'nowrap',
      wordWrap: viewport.isSmallMobile ? 'break-word' : 'normal',
      letterSpacing: '0.01em'
    }
  };

  return (
    <div style={styles.container}>
      <div ref={painPointRef} style={styles.painPoint}>
        {currentPainPoint.text}
      </div>
    </div>
  );
};

export default PainPointSequence;
